import { ActionIcon, Center, Loader } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import clsx from 'clsx'
import { useScroll } from '../common/components/scroll/scroll'
import Meta from '../common/Meta'
import Header from '../common/components/header/Header'
import classes from './Races.module.css'
import { Typography } from '../common/typography'
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import { useCompetitionRounds } from '../matches/api'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import dayjs from 'dayjs'
import { useDivisions } from '../ladder/api'
import { useRoundDivisionParams } from '../common/hooks'
import { useRaces } from './api'
import RaceResult from './RaceResult'
import KpiRoundResults from './KpiRoundResults'
import { Dropdown } from '../common/components/dropdown/Dropdown'
import Tabs from '../stories/Tabs/Tabs'

type RacesTab = 'fixtures' | 'kpi'

interface RacesListProps {
  competitionId: number
  roundNumber?: number
  division?: number
}

function RacesList({ competitionId, roundNumber, division }: RacesListProps) {
  const { data: races } = useRaces({
    competition_id: competitionId,
    round_start: roundNumber,
    round_end: roundNumber,
    division,
  })

  return (
    <div className={classes.racesResultsContainer}>
      {races.map((race) => (
        <RaceResult
          key={race.race_id}
          race={race}
          baseLink={`/competitions/${competitionId}/races/${race.race_id}`}
        />
      ))}
    </div>
  )
}

function RacesListLoader() {
  return (
    <div className={classes.match_results_container}>
      <Center p="xl">
        <Loader color="#0B6B7B" />
      </Center>
    </div>
  )
}

export default function Races() {
  const { competitionId } = useParams()
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const scroll = useScroll()

  const { data: rounds } = useCompetitionRounds({
    competition_id: Number(competitionId),
  })

  const { data: divisions } = useDivisions({
    competition_id: Number(competitionId),
  })

  const { currentRound, currentDivision, setRound, setDivision } =
    useRoundDivisionParams(rounds)

  const selectData = useMemo(
    () =>
      rounds.map((round) => ({
        label: `Race ${round.round_number}`,
        value: round.round_number.toString(),
      })),
    [rounds]
  )

  const divisionData = useMemo(
    () => [
      { label: 'All divisions', value: '' },
      ...(divisions || []).map((division) => ({
        label: division.name,
        value: division.division_id.toString(),
      })),
    ],
    [divisions]
  )

  const currentRoundData = useMemo(
    () => rounds.find((r) => r.round_number === currentRound),
    [rounds, currentRound]
  )

  const showKpi = currentRoundData?.status !== 'upcoming'
  const [activeTab, setActiveTab] = useState<RacesTab>('fixtures')
  useEffect(() => {
    if (!showKpi && activeTab === 'kpi') {
      setActiveTab('fixtures')
    }
  }, [showKpi, activeTab])

  const racesTabs = useMemo(
    () =>
      showKpi
        ? [
            { value: 'fixtures', label: 'Schedule & Results' },
            { value: 'kpi', label: 'KPI Rank' },
          ]
        : [{ value: 'fixtures', label: 'Schedule & Results' }],
    [showKpi]
  )

  const minRound = rounds[0]?.round_number ?? null
  const maxRound = rounds[rounds.length - 1]?.round_number ?? null

  const handlePrevRound = useCallback(() => {
    if (currentRound && minRound && currentRound > minRound) {
      setRound(currentRound - 1)
    }
  }, [currentRound, minRound, setRound])

  const handleNextRound = useCallback(() => {
    if (currentRound && maxRound && currentRound < maxRound) {
      setRound(currentRound + 1)
    }
  }, [currentRound, maxRound, setRound])

  return (
    <div className={classes.container}>
      <Meta title="Races" />
      <Header title="Races" />
      <div
        className={clsx(classes.racesWrapper, {
          [classes.racesWrapperMobile]: !isDesktop,
          [scroll]: !isDesktop,
        })}
      >
        <div className={classes.races}>
          <div
            className={clsx(classes.racesHeader, {
              [classes.racesHeaderMobile]: !isDesktop,
            })}
          >
            {isDesktop ? (
              <Typography
                level={2}
                fw={500}
                lh="125"
                fc={{ light: 'black', dark: 'gray-2' }}
              >
                Schedule & Results
              </Typography>
            ) : (
              <Tabs
                items={racesTabs}
                value={activeTab}
                onChange={(v) => setActiveTab(v as RacesTab)}
                isSmall
              />
            )}

            <div className={classes.filtersContainer}>
              <Dropdown
                placeholder="Division"
                value={currentDivision?.toString() ?? ''}
                onChange={(value) => setDivision(value ? Number(value) : null)}
                data={divisionData}
                rightSection={
                  <IconChevronDown className={classes.selectIcon} />
                }
                styles={{ root: { maxWidth: '10rem' } }}
              />

              <div className={classes.roundContainer}>
                <ActionIcon
                  variant="outline"
                  color="gray"
                  className={classes.actionIcon}
                  onClick={handlePrevRound}
                  disabled={
                    !currentRound || !minRound || currentRound <= minRound
                  }
                >
                  <IconChevronLeft size={20} />
                </ActionIcon>
                <Dropdown
                  placeholder="Race"
                  value={currentRound?.toString() ?? null}
                  onChange={(value) => setRound(value ? Number(value) : null)}
                  data={selectData}
                  rightSection={
                    <IconChevronDown className={classes.selectIcon} />
                  }
                  styles={{ root: { maxWidth: '7rem' } }}
                />

                <ActionIcon
                  variant="outline"
                  color="gray"
                  className={classes.actionIcon}
                  onClick={handleNextRound}
                  disabled={
                    !currentRound || !maxRound || currentRound >= maxRound
                  }
                >
                  <IconChevronRight size={20} />
                </ActionIcon>
              </div>
            </div>
          </div>

          <div
            className={clsx(classes.dataWrapper, {
              [classes.dataWrapperMobile]: !isDesktop,
            })}
          >
            <div className={classes.roundHeader}>
              <Typography
                fs="xs"
                fc={{ light: 'black', dark: 'gray-3' }}
                fw={500}
                lh="130"
              >
                {currentRound ? `Race ${currentRound}` : 'All races'}
              </Typography>
              <Typography
                fs="xs"
                fc={{ light: 'black', dark: 'gray-3' }}
                fw={500}
                lh="130"
                ta="right"
              >
                {currentRoundData
                  ? `${dayjs(currentRoundData.round_start).format('D MMM')} - ${dayjs(currentRoundData.round_start).add(6, 'day').format('D MMM')}`
                  : ''}
              </Typography>
            </div>

            <div className={classes.racesDataWrapper}>
              {(isDesktop || activeTab === 'fixtures') && (
                <div className={classes.racesContainer}>
                  <Suspense fallback={<RacesListLoader />}>
                    <RacesList
                      competitionId={Number(competitionId)}
                      roundNumber={currentRound ?? undefined}
                      division={currentDivision ?? undefined}
                    />
                  </Suspense>
                </div>
              )}
              {showKpi && (isDesktop || activeTab === 'kpi') && (
                <div
                  className={clsx(classes.kpiRoundResultsContainer, {
                    [classes.kpiRoundResultsContainerMobile]: !isDesktop,
                  })}
                >
                  <KpiRoundResults
                    competitionId={Number(competitionId)}
                    roundNumber={currentRound}
                    roundStart={currentRoundData?.round_start}
                    division={currentDivision ?? undefined}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
